import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../api';
import PublicNoticePage from './common/PublicNoticePage';
import ResolvedImage from './shared/ResolvedImage';
import FileLink from './shared/FileLink';
import { MapLinkProvider } from './chat/canvas/MapLinkContext';
import ProjectAppTableBlock from './chat/canvas/ProjectAppTableBlock';
import ProjectAppMapBlock from './chat/canvas/ProjectAppMapBlock';
import ProjectAppTimelineBlock from './chat/canvas/ProjectAppTimelineBlock';
import ProjectAppEmailContentBlock from './chat/canvas/ProjectAppEmailContentBlock';
import './shared-project-app-page.scss';
import './chat/canvas/project-app-blocks.scss';

interface Block {
  id: string;
  type: string;
  row: number;
  column: number;
  rowSpan: number;
  colSpan: number;
  data: Record<string, unknown>;
}

/** Ensure single \n renders as a hard line break in Markdown (two trailing spaces). */
const mdBreaks = (text: string) => text.replace(/\n/g, '  \n');

const PASTEL_COLORS: Record<string, string> = {
  gray: '#F3F3F5',
  yellow: '#FFF7CC',
  green: '#D2F9F4',
  blue: '#D5E9F6',
  white: '#FFFFFF',
  red: '#FBD0D9',
};

const PASTEL_TEXT_COLORS: Record<string, string> = {
  gray: '#838B9B',
  red: '#5E081A',
  green: '#0C5A50',
  blue: '#255B8F',
  yellow: '#665600',
  white: '#0d1f16',
};

/**
 * Read-only block renderer for the public shared page.
 * Avoids importing auth-dependent components (data_table, kanban, stacked_data).
 */
const SharedBlockContent: React.FC<{ block: Block }> = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const { text, level, color } = block.data as {
        text?: string;
        level?: 1 | 2 | 3;
        color?: string;
      };
      const headingLevel = level || 1;
      const style: React.CSSProperties | undefined =
        color && PASTEL_COLORS[color]
          ? {
              backgroundColor: PASTEL_COLORS[color],
              color: PASTEL_TEXT_COLORS[color] || undefined,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }
          : undefined;
      const className = `project-app-block-heading project-app-block-heading--h${headingLevel}`;
      return React.createElement(
        `h${headingLevel}`,
        { className, style },
        text || '',
      );
    }
    case 'markdown': {
      const { content } = block.data as { content?: string };
      return (
        <div className='project-app-block-markdown'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {mdBreaks(content || '')}
          </ReactMarkdown>
        </div>
      );
    }
    case 'table':
      return <ProjectAppTableBlock data={block.data} />;
    case 'map':
      return <ProjectAppMapBlock data={block.data} />;
    case 'timeline':
      return <ProjectAppTimelineBlock data={block.data} />;
    case 'email_content':
      return (
        <ProjectAppEmailContentBlock
          blockId={block.id}
          data={block.data}
          hideGmailLink
        />
      );
    case 'image': {
      const { src, alt, caption } = block.data as {
        src?: string;
        alt?: string;
        caption?: string;
      };
      return (
        <div className='project-app-block-image'>
          {src ? (
            <ResolvedImage src={src} alt={alt || ''} />
          ) : (
            <div className='project-app-block-image__placeholder'>No image</div>
          )}
          {caption && (
            <p className='project-app-block-image__caption'>{caption}</p>
          )}
        </div>
      );
    }
    case 'number': {
      const { value, label, color } = block.data as {
        value?: number | string;
        label?: string;
        color?: string;
      };
      const bgColor =
        color && PASTEL_COLORS[color] ? PASTEL_COLORS[color] : undefined;
      const textColor =
        color && PASTEL_TEXT_COLORS[color]
          ? PASTEL_TEXT_COLORS[color]
          : undefined;
      return (
        <div
          className='project-app-block-number'
          style={
            bgColor ? { backgroundColor: bgColor, color: textColor } : undefined
          }
        >
          <span className='project-app-block-number__value'>
            {value != null ? value : '—'}
          </span>
          {label && (
            <span className='project-app-block-number__label'>{label}</span>
          )}
        </div>
      );
    }
    case 'card': {
      const { image, heading, text, subText, links, farRightText, layout } =
        block.data as {
          image?: string;
          heading?: string;
          text?: string;
          subText?: string;
          links?: { label: string; url: string }[];
          farRightText?: string;
          layout?: 'vertical' | 'horizontal';
        };
      const cardClass = `project-app-block-card${layout === 'horizontal' ? ' project-app-block-card--horizontal' : ''}`;
      return (
        <div className={cardClass}>
          {image && (
            <div className='project-app-block-card__image'>
              <ResolvedImage
                src={image}
                alt={heading || ''}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalHeight > img.naturalWidth) {
                    img.classList.add('portrait');
                  }
                }}
              />
            </div>
          )}
          <div className='project-app-block-card__body'>
            <div className='project-app-block-card__main'>
              {heading && (
                <h3 className='project-app-block-card__heading'>{heading}</h3>
              )}
              {text && (
                <div className='project-app-block-card__text'>
                  <ReactMarkdown>{mdBreaks(text)}</ReactMarkdown>
                </div>
              )}
              {subText && (
                <div className='project-app-block-card__subtext'>
                  <ReactMarkdown>{mdBreaks(subText)}</ReactMarkdown>
                </div>
              )}
              {links && links.length > 0 && (
                <div className='project-app-block-card__links'>
                  {links.map((link, i) => (
                    <FileLink key={i} url={link.url}>
                      {link.label}
                    </FileLink>
                  ))}
                </div>
              )}
            </div>
            {farRightText && (
              <span className='project-app-block-card__far-right'>
                {farRightText}
              </span>
            )}
          </div>
        </div>
      );
    }
    case 'itinerary': {
      const categories = (block.data as any)?.categories;
      if (!Array.isArray(categories) || categories.length === 0) {
        return <div className='project-app-block-unknown'>Empty itinerary</div>;
      }
      return (
        <div className='project-app-block-itinerary-readonly'>
          {categories.map((cat: any, ci: number) => (
            <div key={ci} className='itinerary-category'>
              {cat.name && <h3>{cat.name}</h3>}
              {Array.isArray(cat.items) &&
                cat.items.map((item: any, ii: number) => (
                  <div key={ii} className='itinerary-item'>
                    {item.title && <strong>{item.title}</strong>}
                    {item.description && <p>{item.description}</p>}
                  </div>
                ))}
            </div>
          ))}
        </div>
      );
    }
    case 'data_table':
    case 'kanban':
    case 'stacked_data':
      return (
        <div className='project-app-block-readonly-placeholder'>
          <p>Interactive content — view the full page to interact</p>
        </div>
      );
    default:
      return (
        <div className='project-app-block-unknown'>
          Unknown block type: {block.type}
        </div>
      );
  }
};

export interface SharedAppData {
  name: string;
  description: string | null;
  heroImage: string | null;
  heroImageCaption: string | null;
  blocks: Block[];
}

/**
 * Reusable renderer for project app data (blocks, hero image, header).
 * Used by both the /shared/ route and /artifact/ route for information_dump artifacts.
 */
export const ProjectAppRenderer: React.FC<{ appData: SharedAppData }> = ({
  appData,
}) => {
  const { blocks } = appData;
  const maxRow =
    blocks.length > 0
      ? blocks.reduce((max, b) => Math.max(max, b.row + b.rowSpan), 0)
      : 0;

  return (
    <div className='shared-project-app-page'>
      <MapLinkProvider>
        <div className='project-app-page'>
          {appData.heroImage && (
            <div className='project-app-hero-header'>
              <ResolvedImage src={appData.heroImage} alt={appData.name || ''} />
              {appData.name && (
                <h1 className='project-app-hero-title'>{appData.name}</h1>
              )}
              <div className='project-app-hero-divider' />
            </div>
          )}
          {!appData.heroImage && appData.name && (
            <div className='shared-project-app-header'>
              <h1>{appData.name}</h1>
              {appData.description && <p>{appData.description}</p>}
            </div>
          )}
          {blocks.length > 0 && (
            <div
              className='project-app-blocks'
              style={{
                gridTemplateRows: `repeat(${maxRow}, auto)`,
              }}
            >
              {blocks.map((block) => {
                const extraClass =
                  block.type === 'heading'
                    ? ` project-app-block--heading-h${(block.data as any)?.level || 1}`
                    : '';
                return (
                  <div
                    key={block.id}
                    className={`project-app-block project-app-block--${block.type}${extraClass}`}
                    style={
                      {
                        gridRow: `${block.row + 1} / span ${block.rowSpan}`,
                        gridColumn: `${block.column + 1} / span ${block.colSpan}`,
                        '--col-span': block.colSpan,
                        '--col-span-half': Math.ceil(block.colSpan / 2),
                      } as React.CSSProperties
                    }
                  >
                    <SharedBlockContent block={block} />
                  </div>
                );
              })}
            </div>
          )}
          {blocks.length === 0 && (
            <div className='shared-project-app-empty'>
              <p>This page doesn't have any content yet.</p>
            </div>
          )}
        </div>
      </MapLinkProvider>
      <div className='shared-project-app-footer'>
        <span>Shared via Dream</span>
      </div>
    </div>
  );
};

const SharedProjectAppPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [appData, setAppData] = useState<SharedAppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Override meta description for shared pages
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const original = meta?.getAttribute('content') || '';
    if (meta) {
      meta.setAttribute('content', 'Created by my AI Secretary');
    }
    return () => {
      if (meta) meta.setAttribute('content', original);
    };
  }, []);

  useEffect(() => {
    const fetchSharedApp = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/public/project-apps/${shareToken}`);

        if (response.data?.success && response.data.app) {
          setAppData(response.data.app);
        } else {
          setError('This shared page could not be found');
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError(
            'This shared page could not be found or is no longer available',
          );
        } else {
          setError('Failed to load shared page');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedApp();
  }, [shareToken]);

  if (loading) {
    return (
      <div className='shared-project-app-page'>
        <div className='shared-project-app-loading'>
          <div className='loading-spinner' />
        </div>
      </div>
    );
  }

  if (error || !appData) {
    return (
      <PublicNoticePage
        title={error || 'Page not found'}
        body='The link you followed may be broken or the page may have been unshared.'
      />
    );
  }

  return <ProjectAppRenderer appData={appData} />;
};

export default SharedProjectAppPage;
